import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailReturns, type RetailReturnListItem } from '@/api/retailReturns';
import type { FiscalStatus } from '@/api/retailSales';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { useAttachmentFlags } from '@/shared/hooks/useAttachmentFlags';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.return.create';

const FISCAL_LABEL: Record<string, string> = {
  not_fiscalized: '— Не фіскалізовано',
  pending: '⏳ В очікуванні',
  fiscalized: '✓ Фіскалізовано',
  failed: '⚠ Помилка',
  demo: 'D Демо',
};

const METHOD_LABEL: Record<string, string> = {
  cash: 'Готівка',
  card: 'На картку',
  bonus: 'Бонусами',
  mixed: 'Змішана',
};

export default function RetailReturnsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  const [fiscalFilter, setFiscalFilter] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-returns', orgId, fiscalFilter],
    queryFn: () =>
      retailReturns.list({
        organization_id: orgId || undefined,
        fiscal_status: fiscalFilter ? (fiscalFilter as FiscalStatus) : undefined,
        limit: 500,
      }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<RetailReturnListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата/час',
        width: 180,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'original_sale_id',
        header: 'Оригінал чека',
        sortValue: (r) => r.original_sale_id ?? '',
        cell: (r) => r.original_sale_id,
      },
      {
        key: 'return_amount',
        header: 'Сума',
        width: 130,
        align: 'right',
        sortValue: (r) => Number(r.return_amount),
        cell: (r) =>
          r.return_amount
            ? Number(r.return_amount).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '',
      },
      {
        key: 'return_method',
        header: 'Метод',
        width: 130,
        sortValue: (r) => METHOD_LABEL[r.return_method] ?? r.return_method ?? '',
        cell: (r) => METHOD_LABEL[r.return_method] ?? r.return_method,
      },
      {
        key: 'fiscal_status',
        header: 'ПРРО',
        width: 170,
        sortValue: (r) => FISCAL_LABEL[r.fiscal_status] ?? r.fiscal_status ?? '',
        cell: (r) => FISCAL_LABEL[r.fiscal_status] ?? r.fiscal_status,
      },
      {
        key: 'fiscal_number',
        header: 'Фіск. №',
        width: 140,
        sortValue: (r) => r.fiscal_number ?? '',
        cell: (r) => r.fiscal_number,
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати повернення (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  // 2026-08-23: колонки «Вкладення»/«Підпис» у списку документів.
  const _rowsForCols = list.data?.items ?? [];
  const att = useAttachmentFlags<RetailReturnListItem>('doc.retail_returns', _rowsForCols);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Повернення від покупця (роздріб)
          <HintBadge
            title="Що це?"
            text={
              'Документ повернення товару від покупця у роздробі. ' +
              'Обов\'язково посилається на оригінальний чек продажу.\n\n' +
              'Що відбувається при проведенні:\n' +
              '• Товар повертається на склад (direction=«+»).\n' +
              '• Гроші повертаються (готівка / на картку / бонусами / змішане).\n' +
              '• Бонуси, нараховані за оригінальний чек, СПИСУЮТЬСЯ пропорційно.\n' +
              '• Якщо клієнт списав бонуси при покупці — вони повертаються на картку.\n' +
              '• Фіскальний чек повернення йде у ПРРО (окремий тип).\n\n' +
              'Не плутати з «Поверненням постачальнику» (це по покупках).\n\n' +
              'Можна повернути частково (тільки частину рядків) — backend контролює ' +
              'що сукупна кількість повернень не перевищить оригінал.'
            }
          />
        </h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <select
          value={fiscalFilter}
          onChange={(e) => setFiscalFilter(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Всі статуси ПРРО</option>
          <option value="not_fiscalized">Не фіскалізовано</option>
          <option value="pending">В очікуванні</option>
          <option value="fiscalized">Фіскалізовано</option>
          <option value="failed">Помилка</option>
          <option value="demo">Демо</option>
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/retail-returns/new')}
        >
          + Нове повернення
        </button>
      </div>
      <DataTable<RetailReturnListItem>
        rows={list.data?.items ?? []}
        columns={[...cols, ...att.column]}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/retail/retail-returns/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
